import { http } from '../api/client'
import type { LoginResponse } from '../api/types'

/**
 * Resposta do login quando o segundo fator está ativo.
 *
 * A senha correta NÃO cria sessão: devolve só este desafio, que sozinho não
 * autentica nada. Por isso não existe token nenhum aqui.
 */
export interface MfaChallengeResponse {
  mfa_required: true
  challenge: string
  expires_in: number
}

export type LoginOutcome = LoginResponse | MfaChallengeResponse

export function isMfaChallenge(resposta: LoginOutcome): resposta is MfaChallengeResponse {
  return (resposta as MfaChallengeResponse)?.mfa_required === true
}

export interface MfaStatus {
  enabled: boolean
  required: boolean
  setup_required: boolean
  recovery_codes_left: number
  confirmed_at: string | null
}

export interface MfaSetup {
  secret: string
  otpauth_uri: string
  issuer: string
}

export const mfaApi = {
  /** Segunda etapa do login: troca desafio mais código por uma sessão. */
  verify(challenge: string, codigo: string): Promise<LoginResponse> {
    return http.post<LoginResponse>('/auth/mfa/verify', { challenge, codigo })
  },
  status(): Promise<MfaStatus> {
    return http.get<MfaStatus>('/auth/mfa/status')
  },
  /** Gera o segredo. Ainda não ativa: falta confirmar o primeiro código. */
  setup(): Promise<MfaSetup> {
    return http.post<MfaSetup>('/auth/mfa/setup', {})
  },
  /** Confirma o primeiro código e devolve os códigos de recuperação, uma única vez. */
  confirm(codigo: string): Promise<{ enabled: boolean; recovery_codes: string[] }> {
    return http.post<{ enabled: boolean; recovery_codes: string[] }>('/auth/mfa/confirm', { codigo })
  },
  disable(senha: string): Promise<{ enabled: boolean }> {
    return http.post<{ enabled: boolean }>('/auth/mfa/disable', { senha })
  },
  regenerateRecoveryCodes(senha: string): Promise<{ recovery_codes: string[] }> {
    return http.post<{ recovery_codes: string[] }>('/auth/mfa/recovery-codes', { senha })
  },
}

/**
 * Quebra o segredo em blocos de quatro, para quem digita à mão no autenticador
 * não se perder. O Google Authenticator aceita a chave com espaços.
 */
export function segredoLegivel(segredo: string): string {
  return (segredo.match(/.{1,4}/g) ?? []).join(' ')
}
